import {useCallback, useRef} from 'react'

/**
 * CSS custom property that lets a sticky element keep the header from showing
 * again while the element is pinned. Only the value `1` turns the lock on.
 */
export const HEADER_REVEAL_LOCK_VAR = '--header-reveal-lock'

const PIN_TOLERANCE_PX = 1
let lockedElement = null

/**
 * Whether the registered sticky element is pinned and blocks the header reveal.
 *
 * Called on every scroll-up frame. Without the lock var set to `1` it returns
 * early and does not touch the layout.
 *
 * @returns {boolean} `true` while the element is pinned and the lock is on.
 *
 * @example
 * const hidden = useHideOnScroll({threshold, isRevealLocked: isHeaderRevealLocked})
 */
export const isHeaderRevealLocked = () => {
    if (!lockedElement || typeof window === 'undefined') return false

    const styles = window.getComputedStyle(lockedElement)
    if (styles.getPropertyValue(HEADER_REVEAL_LOCK_VAR).trim() !== '1') return false

    // A sticky element is pinned while it sits on its `top` value. Above or
    // below that line it is not pinned, e.g. once it scrolls out with its
    // container.
    const pinLine = parseFloat(styles.top)
    // `top: auto` is NaN and never counts as pinned.
    if (Number.isNaN(pinLine)) return false

    return Math.abs(lockedElement.getBoundingClientRect().top - pinLine) <= PIN_TOLERANCE_PX
}

/**
 * Registers the sticky element that may block the header reveal while pinned.
 *
 * Only stores the element. `isHeaderRevealLocked` checks the DOM, so this adds
 * no renders and no scroll listener.
 *
 * @returns {Function} Ref callback for the sticky element.
 *
 * @example
 * const setStickyGallery = useHeaderRevealLock()
 * return (
 *     <Box
 *         ref={setStickyGallery}
 *         position="sticky"
 *         top={{base: 'spacing-8', s: 'spacing-32'}}
 *         css={{[HEADER_REVEAL_LOCK_VAR]: {base: '1', s: '0'}}}
 *     />
 * )
 */
export const useHeaderRevealLock = () => {
    const registered = useRef(null)

    return useCallback((node) => {
        if (node) {
            lockedElement = node
        } else if (lockedElement === registered.current) {
            // Only clear our own element, not one registered after it.
            lockedElement = null
        }
        registered.current = node
    }, [])
}
